import { ScaleElement } from '@scale/base-element';

class ScaleHeader extends ScaleElement {
  #scrollY = 0;
  #headerHeight = 0;
  #sentinel;
  #stickyObserver;
  #resizeObserver;

  setup() {
    const sticky = this.dataset.sticky || 'none';

    this.#measureHeight();
    this.#setupResizeObserver();

    if (sticky !== 'none') {
      this.#setupSticky(sticky);
    }

    this.onDocument('shopify:cart:lines-update', (event) => {
      event.promise?.then(({ cart }) => this.#updateCartCount(cart)).catch(() => {});
    });

    this.onDocument('shopify:section:load', () => {
      this.#measureHeight();
    });
  }

  teardown() {
    this.#stickyObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#sentinel?.remove();
  }

  updatedCallback() {
    this.invalidateRefs();
    this.#measureHeight();
  }

  // --- Sticky ---

  #setupSticky(mode) {
    this.#sentinel = document.createElement('div');
    this.#sentinel.style.cssText = 'height:1px;width:100%;pointer-events:none;margin-block-end:-1px';
    const section = this.closest('.section-header') || this.parentElement;
    section.parentElement.insertBefore(this.#sentinel, section);

    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        const scrolled = window.scrollY > 1 && !entry.isIntersecting;
        this.classList.toggle('is-scrolled', scrolled);
      },
      { threshold: 0 }
    );
    this.#stickyObserver.observe(this.#sentinel);

    if (mode === 'on_scroll_up') {
      this.#setupScrollDirection();
    }
  }

  #setupScrollDirection() {
    this.#scrollY = window.scrollY;

    window.addEventListener('scroll', () => {
      const currentY = window.scrollY;
      const delta = currentY - this.#scrollY;

      if (currentY <= this.#headerHeight) {
        this.classList.remove('is-hidden');
      } else if (delta > 15) {
        this.classList.add('is-hidden');
      } else if (delta < -15) {
        this.classList.remove('is-hidden');
      }

      this.#scrollY = currentY;
    }, { signal: this.signal, passive: true });
  }

  // --- Height tracking ---

  #measureHeight() {
    this.#headerHeight = this.offsetHeight;
    const value = `${this.#headerHeight}px`;
    // Write onto .section-header (where the Liquid estimate lives) so the
    // measured value overrides it at the same scope the immersive pull-up
    // reads from. Mirror to <html> for anything reading it globally.
    const section = this.closest('.section-header');
    section?.style.setProperty('--header-height', value);
    document.documentElement.style.setProperty('--header-height', value);
  }

  #setupResizeObserver() {
    this.#resizeObserver = new ResizeObserver(() => {
      this.debounce('resize', () => this.#measureHeight(), 100);
    });
    this.#resizeObserver.observe(this);
  }

  // --- Cart count ---

  #updateCartCount(cart) {
    const badge = this.ref('cart-count');
    if (!badge) return;
    const count = cart?.totalQuantity ?? 0;
    badge.textContent = count;
    badge.hidden = count === 0;
  }

  // --- Search overlay ---

  openSearch() {
    this.querySelector('scale-search-overlay')?.openSearch();
  }

  closeSearch() {
    this.querySelector('scale-search-overlay')?.closeSearch();
  }

  clearSearch() {
    this.querySelector('scale-search-overlay')?.clearSearch();
  }

  // --- Drawers ---

  async openDrawer() {
    await this.#openElement('scale-drawer', document.getElementById('header-drawer'));
  }

  async openCartDrawer(event) {
    event.preventDefault();
    await this.#openElement('scale-cart-drawer', document.querySelector('scale-cart-drawer'));
  }

  async #openElement(tagName, element) {
    if (!element) return;
    if (!customElements.get(tagName)) {
      await customElements.whenDefined(tagName);
    }
    element.open();
  }

  // --- Localization ---

  submitLocalization(event) {
    event.target.closest('form')?.submit();
  }

  // --- Design mode ---

  onBlockSelect() {
    document.getElementById('header-drawer')?.open?.();
  }

  onBlockDeselect() {
    document.getElementById('header-drawer')?.close?.();
  }
}

if (!customElements.get('scale-header')) {
  customElements.define('scale-header', ScaleHeader);
}
